import * as React from 'react';
import { useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { MainLayout } from '@/layouts/main';

import { EventDetail } from '@/features/event-page/detail';
import { EventType, mapPublicEventCallToEvent } from '@/features/events-page/list/events.const';

import { getPublicEventCall } from '@/shared/api/event-public';
import { loadActivityVenue, type PublicActivityVenue } from '@/shared/api/public-activity-venue';
import { getPublicTournamentDetail } from '@/shared/api/tournament-public';
import { getEventSocialImage } from '@/shared/lib/event-images';
import { DEFAULT_LOCALE } from '@/shared/lib/i18n';
import { getTranslationsConfig } from '@/shared/lib/i18n/translations';
import {
  APP_URL,
  createSportsEventStructuredData,
  getActivityPath,
  getActivitySeoTitle,
  getHttpStatusCode,
  getLocalizedPath,
  getLocalizedUrl,
  getMetaImageUrl,
  isPublicActivityId,
  PageSEO,
  resolveLegacyActivity,
} from '@/shared/lib/meta';
import {
  createAppDeepLink,
  createMobileAppLaunchLink,
  createStoreUrl,
  IOS_APP_STORE_URL,
  rememberInstallLink,
} from '@/shared/lib/mobile-app-linking';
import { EnhancedNextPage } from '@/shared/types/enhanced-next-page';
import { SSRPageProps } from '@/shared/types/ssr-page-props';

type IndexPageProps = SSRPageProps<{
  canonicalUrl: string;
  event: EventType | null;
  venue?: PublicActivityVenue | null;
}>;

const EVENT_SHARE_IMAGE_HEIGHT = 630;
const EVENT_SHARE_IMAGE_WIDTH = 1200;
const FALLBACK_SEO_TITLE = 'Sportyfinder';
const FALLBACK_SEO_DESCRIPTION = 'Событие временно недоступно. Найдите другие игры и тренировки рядом в Sportyfinder.';

const KIND_TITLE_LABELS: Record<EventType['eventKind'], string> = {
  Challenge: 'вызов',
  Event: 'событие',
  Tournament: 'турнир',
};

function replaceUnsafeMetaCharacter(character: string) {
  const codePoint = character.codePointAt(0) ?? 0;
  const isControlCharacter = codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
  const isFormattingCharacter =
    (codePoint >= 0x200b && codePoint <= 0x200f) ||
    (codePoint >= 0x202a && codePoint <= 0x202e) ||
    (codePoint >= 0x2060 && codePoint <= 0x206f) ||
    codePoint === 0xfeff;

  return isControlCharacter || isFormattingCharacter ? ' ' : character;
}

function trimMetaText(value: string, maxLength = 180) {
  const normalized = Array.from(value.normalize('NFC'), replaceUnsafeMetaCharacter)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trim()}…`;
}

function getEventSeoDescription(event: EventType) {
  const sportName = event.sportType?.name || 'спорт';
  const date = event.dates[0] ? `${event.dates[0]}. ` : '';
  const address = event.contacts.address ? `${event.contacts.address}. ` : '';

  if (event.eventKind === 'Tournament' && event.tournament) {
    const maxTeams = event.tournament.settings?.maxTeams;
    const teams = maxTeams
      ? `${event.tournament.registeredTeamsCount} из ${maxTeams} команд`
      : `${event.tournament.registeredTeamsCount} команд`;

    return trimMetaText(
      `${event.title}: турнир, спорт — ${sportName}. ${date}${address}Собрано ${teams}, максимум участников — ${
        event.maxParticipants ?? 'уточняется'
      }. Команды, расписание и результаты в Sportyfinder.`,
    );
  }

  if (event.eventKind === 'Challenge') {
    const target = event.victoryCondition?.targetValue || event.victoryTargetValue;

    return trimMetaText(
      `${event.title}: спортивный вызов в Sportyfinder. ${date}${address}${
        target ? `Цель — ${target}. ` : ''
      }Присоединяйся и отслеживай прогресс участников.`,
    );
  }

  return trimMetaText(
    `${event.title}: спортивное событие, спорт — ${sportName}. ${date}${address}Записалось ${
      event.participantsTotal ?? 0
    }, максимум участников — ${event.maxParticipants ?? 'уточняется'}.`,
  );
}

function getEventStructuredData(event: EventType, canonicalUrl: string, imageUrl: string) {
  return createSportsEventStructuredData({
    name: event.title,
    eventKind: event.eventKind,
    description: getEventSeoDescription(event),
    imageUrl,
    canonicalUrl,
    startDate: event.startDate,
    endDate: event.endDate,
    status: event.tournamentStatus || event.status,
    maximumAttendeeCapacity: event.maxParticipants,
    address: event.contacts.address,
    locationName: event.contacts.address,
    organizerName: event.authorName || 'Sportyfinder',
    organizerType: event.authorName?.includes('Sportyfinder') ? 'Organization' : 'Person',
    sport: event.sportType?.name,
  });
}

function isMobileUserAgent(userAgent: string) {
  return /Android|iPhone|iPad|iPod/i.test(userAgent);
}

export const getServerSideProps: GetServerSideProps<IndexPageProps> = async (context) => {
  const id = context.params?.id;
  let event: EventType | null = null;

  if (typeof id !== 'string') {
    return { notFound: true };
  }

  const canonicalPath = getActivityPath({ eventKind: 'Event', id });
  const canonicalUrl = getLocalizedUrl(canonicalPath, DEFAULT_LOCALE, DEFAULT_LOCALE);
  const legacyActivity = resolveLegacyActivity(id);

  if (legacyActivity?.kind === 'moved') {
    return {
      redirect: {
        destination: getLocalizedPath(legacyActivity.destination, context.locale, DEFAULT_LOCALE),
        permanent: true,
      },
    };
  }

  if (legacyActivity?.kind === 'gone') {
    context.res.statusCode = 410;
    context.res.setHeader('Cache-Control', 'no-store');
    context.res.setHeader('X-Robots-Tag', 'noindex, nofollow');
    const i18n = await getTranslationsConfig(context, ['event', 'common']);

    return {
      props: {
        canonicalUrl,
        isServerRendering: true,
        event: null,
        _nextI18Next: i18n._nextI18Next,
      },
    };
  }

  if (!isPublicActivityId(id)) {
    return { notFound: true };
  }

  try {
    const response = await getPublicEventCall(id);
    event = mapPublicEventCallToEvent(response.data.data);

    if (event.eventKind !== 'Event') {
      return {
        redirect: {
          destination: getLocalizedPath(getActivityPath(event), context.locale, DEFAULT_LOCALE),
          permanent: true,
        },
      };
    }

    context.res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=86400');
  } catch (error) {
    const eventStatus = getHttpStatusCode(error);

    if (eventStatus === 404) {
      try {
        await getPublicTournamentDetail(id, context.locale ?? DEFAULT_LOCALE);

        return {
          redirect: {
            destination: getLocalizedPath(
              getActivityPath({ eventKind: 'Tournament', id }),
              context.locale,
              DEFAULT_LOCALE,
            ),
            permanent: true,
          },
        };
      } catch (tournamentError) {
        const tournamentStatus = getHttpStatusCode(tournamentError);

        if (tournamentStatus === 400 || tournamentStatus === 404) {
          return { notFound: true };
        }

        context.res.statusCode = tournamentStatus === 410 ? 410 : 503;
        context.res.setHeader('Cache-Control', 'no-store');
        context.res.setHeader('X-Robots-Tag', 'noindex, nofollow');
      }
    } else if (eventStatus === 400) {
      return { notFound: true };
    } else {
      context.res.statusCode = eventStatus === 410 ? 410 : 503;
      context.res.setHeader('Cache-Control', 'no-store');
      context.res.setHeader('X-Robots-Tag', 'noindex, nofollow');
    }
  }

  const i18n = await getTranslationsConfig(context, ['event', 'common']);

  return {
    props: {
      canonicalUrl,
      isServerRendering: true,
      event,
      venue: event ? await loadActivityVenue(id) : null,
      _nextI18Next: i18n._nextI18Next,
    },
  };
};

const EventDetailPage = ((props: IndexPageProps) => {
  const { t } = useTranslation('event');
  const { canonicalUrl, event, venue } = props;
  const router = useRouter();
  const routeId: unknown = router.query.id;
  const id = typeof routeId === 'string' ? routeId : undefined;
  const [storeUrl, setStoreUrl] = useState(IOS_APP_STORE_URL);
  const eventKind = event?.eventKind ?? 'Event';
  const appPath = id
    ? getActivityPath({ eventKind, id })
    : eventKind === 'Challenge'
    ? '/challenges'
    : eventKind === 'Tournament'
    ? '/tournaments'
    : '/events';
  const deepLinkUrl = createAppDeepLink(appPath);
  const [openAppUrl, setOpenAppUrl] = useState(deepLinkUrl);

  useEffect(() => {
    if (!router.isReady || typeof window === 'undefined' || typeof id !== 'string') {
      return undefined;
    }

    if (router.query.openApp !== '1') {
      return undefined;
    }

    const { userAgent } = window.navigator;
    if (!isMobileUserAgent(userAgent)) {
      return undefined;
    }

    const fallbackUrl = createStoreUrl(appPath, userAgent);
    const launchUrl = createMobileAppLaunchLink(appPath, userAgent, fallbackUrl);
    setStoreUrl(fallbackUrl);
    setOpenAppUrl(launchUrl);
    const fallbackTimeout = window.setTimeout(() => {
      window.location.replace(fallbackUrl);
    }, 1600);

    window.location.href = launchUrl;

    return () => {
      window.clearTimeout(fallbackTimeout);
    };
  }, [appPath, id, router.isReady, router.query.openApp]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const fallbackUrl = createStoreUrl(appPath, window.navigator.userAgent);
    setStoreUrl(fallbackUrl);
    setOpenAppUrl(createMobileAppLaunchLink(appPath, window.navigator.userAgent, fallbackUrl));
  }, [appPath]);

  if (!event) {
    return (
      <>
        <PageSEO
          title={FALLBACK_SEO_TITLE}
          description={FALLBACK_SEO_DESCRIPTION}
          robots="noindex, nofollow"
          url={canonicalUrl}
        />
        <div>Событие не найдено</div>
      </>
    );
  }

  const eventData: EventType = event;
  const title = getActivitySeoTitle(eventData) || FALLBACK_SEO_TITLE;
  const description = getEventSeoDescription(eventData) || FALLBACK_SEO_DESCRIPTION;
  const ogImage = getEventSocialImage(eventData.sportType?.iconKey);
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Sportyfinder', item: getLocalizedUrl('/', 'ru', 'ru') },
      { '@type': 'ListItem', position: 2, name: 'События', item: getLocalizedUrl('/events', 'ru', 'ru') },
      { '@type': 'ListItem', position: 3, name: eventData.title, item: canonicalUrl },
    ],
  };
  const keywords = [
    eventData.title,
    eventData.sportType?.name,
    KIND_TITLE_LABELS[eventData.eventKind],
    eventData.eventKind === 'Tournament' ? 'турнир' : null,
    eventData.eventKind === 'Challenge' ? 'спортивный вызов' : null,
    'спортивное событие Sportyfinder',
    'Sportyfinder',
    eventData.contacts.address,
    eventData.dates.join(', '),
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <PageSEO
        title={title}
        description={description}
        image={ogImage}
        imageAlt={`${eventData.title} — ${KIND_TITLE_LABELS[eventData.eventKind]} в Sportyfinder`}
        imageHeight={EVENT_SHARE_IMAGE_HEIGHT}
        imageWidth={EVENT_SHARE_IMAGE_WIDTH}
        keywords={keywords}
        ogTitle={title}
        ogDescription={description}
        ogImage={ogImage}
        structuredData={[
          getEventStructuredData(eventData, canonicalUrl, getMetaImageUrl(ogImage)),
          breadcrumbStructuredData,
        ]}
        url={canonicalUrl}
      />
      <EventDetail
        downloadAppLabel={t('DOWNLOAD_APP')}
        event={eventData}
        venue={venue}
        openAppLabel={t('OPEN_IN_APP')}
        openAppUrl={openAppUrl}
        storeUrl={storeUrl}
        onDownloadAppClick={() => {
          rememberInstallLink(`${APP_URL}${appPath}`);
        }}
      />
    </>
  );
}) as EnhancedNextPage<IndexPageProps>;

EventDetailPage.Layout = MainLayout;

export default EventDetailPage;
